import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/* Stripe Connect onboarding panel for the restaurant admin.
   - Polls /status/{venue} to surface live progress
   - "Connect with Stripe" button → POST /onboard/{venue} → redirect to Stripe-hosted onboarding
   - On return Stripe pings us back at ?stripe=return → we auto-refresh status
   - Failure modes: friendly messages for the "Connect not enabled on platform" case
*/

const VENUE_DEFAULTS = {
  "osteria-epoca": {
    venue_name: "Osteria Epoca",
    region_slug: "brisbane-south-yeronga",
    state_code: "QLD",
  },
};

function makeReturnUrl(venueSlug, mode) {
  const u = new URL(window.location.origin + "/admin/hustleeats");
  u.searchParams.set("stripe", mode);
  u.searchParams.set("venue", venueSlug);
  return u.toString();
}

export default function StripeConnectPanel({ venueSlug }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [postOnboardMsg, setPostOnboardMsg] = useState("");
  const inflightRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const res = await fetch(`${API}/api/stripe-connect/status/${venueSlug}`, {
        credentials: "include",
      });
      if (res.status === 401) {
        setErrMsg("Sign in to manage payments for this venue.");
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        setErrMsg(data.detail || "Couldn't load payment status.");
        return;
      }
      setStatus(data);
    } finally {
      setLoading(false);
    }
  }, [venueSlug]);

  // Pick up Stripe return after onboarding.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeFlag = params.get("stripe");
    if (stripeFlag === "return") {
      setPostOnboardMsg("Welcome back! Refreshing your Stripe status…");
      // strip the param so a hard refresh doesn't repeat the message
      const url = new URL(window.location.href);
      url.searchParams.delete("stripe");
      url.searchParams.delete("venue");
      window.history.replaceState({}, "", url.toString());
    } else if (stripeFlag === "refresh") {
      setPostOnboardMsg("Your onboarding link expired — generating a fresh one.");
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const startOnboarding = async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    setBusy(true);
    setErrMsg("");
    try {
      const defaults = VENUE_DEFAULTS[venueSlug] || {
        venue_name: venueSlug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        region_slug: "brisbane",
        state_code: "QLD",
      };
      const body = {
        ...defaults,
        return_url: makeReturnUrl(venueSlug, "return"),
        refresh_url: makeReturnUrl(venueSlug, "refresh"),
      };
      const { data } = await axios.post(
        `${API}/api/stripe-connect/onboard/${venueSlug}`,
        body,
        { withCredentials: true },
      );
      window.location.href = data.onboarding_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail) {
        setErrMsg(detail);
      } else {
        console.error("startOnboarding caught:", e);
        setErrMsg("Network error — please try again.");
      }
    } finally {
      setBusy(false);
      inflightRef.current = false;
    }
  };

  const fmtRequirements = (reqs) => {
    if (!reqs) return null;
    const all = [
      ...(reqs.currently_due || []),
      ...(reqs.past_due || []),
    ];
    if (all.length === 0) return null;
    return [...new Set(all)].slice(0, 4);
  };

  if (loading) {
    return (
      <section className="he-admin__panel sc-panel">
        <div className="he-state">Loading payment status…</div>
      </section>
    );
  }

  const connected = status?.connected;
  const onboarded = status?.onboarding_complete;
  const charges = status?.charges_enabled;
  const payouts = status?.payouts_enabled;
  const pendingReqs = fmtRequirements(status?.requirements);

  return (
    <section className="he-admin__panel sc-panel" data-testid="sc-panel">
      <div className="sc-head">
        <div>
          <h2 className="he-admin__h2" style={{ marginBottom: 4 }}>Get paid direct</h2>
          <p className="he-admin__hint" style={{ margin: 0 }}>
            Diner payments land in <strong>your</strong> bank, not ours. We never touch the money — you pay
            a flat monthly fee, no per-cover cut.
          </p>
        </div>
        <div className={`sc-badge sc-badge--${onboarded ? "ok" : connected ? "warn" : "off"}`} data-testid="sc-badge">
          {onboarded ? "Connected · ready" : connected ? "In progress" : "Not connected"}
        </div>
      </div>

      {postOnboardMsg && (
        <div className="he-admin__msg he-admin__msg--ok" data-testid="sc-postonboard">
          {postOnboardMsg}
        </div>
      )}

      {errMsg && (
        <div className="he-admin__msg he-admin__msg--err" data-testid="sc-err">
          {errMsg}
        </div>
      )}

      {!onboarded && (
        <button
          type="button"
          className="he-card__cta sc-btn"
          onClick={startOnboarding}
          disabled={busy}
          data-testid="sc-onboard-btn"
        >
          {busy ? "Opening Stripe…" : connected ? "Finish Stripe onboarding" : "Connect with Stripe"}
        </button>
      )}

      {connected && (
        <ul className="sc-checks" data-testid="sc-checks">
          <li className={charges ? "is-on" : ""}>
            <span className="sc-dot" />Accepting payments
          </li>
          <li className={payouts ? "is-on" : ""}>
            <span className="sc-dot" />Payouts to your bank enabled
          </li>
          {pendingReqs && (
            <li>
              <span className="sc-dot sc-dot--warn" />
              Stripe still needs: {pendingReqs.join(", ")}
            </li>
          )}
        </ul>
      )}

      <button type="button" className="sc-refresh" onClick={fetchStatus} data-testid="sc-refresh">
        Refresh status
      </button>
    </section>
  );
}
